import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CalendarClock,
  CaseSensitive,
  ChevronRight,
  CircleDollarSign,
  IndianRupee,
  LayoutDashboard,
  MonitorCheck,
  PieChart,
  ReceiptIndianRupee,
  WalletCards,
  Wrench,
} from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Layout } from "@/components/Layout";
import { HoardexaLoader } from "@/components/HoardexaLoader";
import { MetricStrip } from "@/components/CommandSystem";
import api, { formatApiError } from "@/lib/api";
import { hasAnyPermission } from "@/lib/permissions";
import { useAuth } from "@/lib/auth";
import { inrPlain } from "@/lib/format";
import { daysUntil, normalizeDashboardData } from "./utils";

const TONES = {
  blue: {
    icon: "bg-sky-50 text-sky-700 ring-sky-100",
    bar: "bg-sky-600",
    text: "text-sky-700",
  },
  green: {
    icon: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    bar: "bg-emerald-500",
    text: "text-emerald-700",
  },
  amber: {
    icon: "bg-amber-50 text-amber-700 ring-amber-100",
    bar: "bg-amber-500",
    text: "text-amber-700",
  },
  rose: {
    icon: "bg-rose-50 text-rose-700 ring-rose-100",
    bar: "bg-rose-500",
    text: "text-rose-700",
  },
  violet: {
    icon: "bg-violet-50 text-violet-700 ring-violet-100",
    bar: "bg-violet-500",
    text: "text-violet-700",
  },
  slate: {
    icon: "bg-slate-100 text-slate-700 ring-slate-200",
    bar: "bg-slate-500",
    text: "text-slate-700",
  },
};

function asNumber(value) {
  return Number(value || 0);
}

function formatCompactMonth(value) {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    const month = date.toLocaleDateString("en-US", { month: "short" });
    return `${month} '${String(date.getFullYear()).slice(-2)}`;
  }
  return String(value).replace(" 20", " '");
}

function tooltipFormatter(value) {
  return [inrPlain(value * 100000), "Revenue"];
}

function formatMoneyWords(value) {
  const amount = Math.abs(asNumber(value));
  if (amount >= 10000000) {
    const crore = amount / 10000000;
    return `${crore >= 10 ? crore.toFixed(1) : crore.toFixed(2).replace(/\.00$/, "")} cr`;
  }
  if (amount >= 100000) {
    const lakh = amount / 100000;
    return `${lakh >= 10 ? lakh.toFixed(1) : lakh.toFixed(2).replace(/\.00$/, "")} lakhs`;
  }
  if (amount >= 1000) {
    const thousand = amount / 1000;
    return `${thousand >= 10 ? thousand.toFixed(0) : thousand.toFixed(1).replace(/\.0$/, "")}k`;
  }
  return amount.toLocaleString("en-IN");
}

function moneyLabel(value, raw) {
  const prefix = asNumber(value) < 0 ? "-" : "";
  return raw ? `${prefix}${inrPlain(Math.abs(asNumber(value)))}` : `${prefix}${formatMoneyWords(value)}`;
}

function AmountSwitch({ raw, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="inline-flex h-7 items-center rounded-full border border-slate-200 bg-slate-100 p-0.5 text-slate-500 hover:border-slate-300 hover:bg-white"
      title={raw ? "Show compact amount words" : "Show exact rupee amounts"}
      aria-label={raw ? "Show compact amount words" : "Show exact rupee amounts"}
      aria-pressed={raw}
    >
      <span className={`flex h-6 w-7 items-center justify-center rounded-full transition ${!raw ? "bg-white text-sky-700 shadow-sm" : ""}`}>
        <CaseSensitive size={13} strokeWidth={2.5} />
      </span>
      <span className={`flex h-6 w-7 items-center justify-center rounded-full transition ${raw ? "bg-white text-sky-700 shadow-sm" : ""}`}>
        <IndianRupee size={12} strokeWidth={2.7} />
      </span>
    </button>
  );
}

function Card({ children, className = "", onClick }) {
  const TagName = onClick ? "button" : "section";

  return (
    <TagName
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`hx-inventory-register min-w-0 text-left transition ${
        onClick ? "hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500" : ""
      } ${className}`}
      style={{ borderColor: "rgba(15, 23, 42, 0.28)" }}
    >
      {children}
    </TagName>
  );
}

function SectionTitle({ title, subtitle, action, onAction }) {
  return (
    <div className="mb-3 flex min-w-0 items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="truncate text-sm font-bold text-slate-950">{title}</h2>
        {subtitle && <p className="mt-0.5 truncate text-[11px] font-medium text-slate-500">{subtitle}</p>}
      </div>
      {action && (
        <button
          type="button"
          onClick={onAction}
          className="inline-flex flex-shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-bold text-sky-700 hover:bg-sky-50"
        >
          {action}
          <ChevronRight size={13} />
        </button>
      )}
    </div>
  );
}

function FinanceTile({ kpis, rawAmounts, onToggleAmounts }) {
  const netProfit = asNumber(kpis.net_profit);
  const positive = netProfit >= 0;

  return (
    <Card className="p-3.5">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <div className="truncate text-[11px] font-bold uppercase tracking-wide text-slate-500">
              Finance snapshot
            </div>
            <AmountSwitch raw={rawAmounts} onToggle={onToggleAmounts} />
          </div>
          <div className="mt-2 font-mono truncate text-xl font-bold leading-none text-slate-950">
            {moneyLabel(kpis.monthly_revenue, rawAmounts)}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] font-semibold font-mono">
            <span className="truncate text-slate-500">Expense {moneyLabel(kpis.total_expenses, rawAmounts)}</span>
            <span className={`truncate text-right ${positive ? "text-emerald-700" : "text-rose-700"} font-bold`}>
              {positive ? "Profit" : "Loss"} {moneyLabel(Math.abs(netProfit), rawAmounts)}
            </span>
          </div>
        </div>
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700 ring-1 ring-slate-200">
          <CircleDollarSign size={17} strokeWidth={2.35} />
        </div>
      </div>
    </Card>
  );
}

function SmartActionGrid({ cards }) {
  if (!cards.length) return null;

  return (
    <section className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
      {cards.slice(0, 6).map((card) => {
        const Icon = card.icon;
        const theme = TONES[card.tone] || TONES.blue;
        return (
          <Card
            key={card.key}
            onClick={card.onClick}
            className="p-3"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ring-1 ${theme.icon}`}>
                <Icon size={16} strokeWidth={2.35} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-baseline gap-2">
                  <strong className="text-xl font-mono font-bold leading-none text-slate-950">{card.value}</strong>
                  <span className="truncate text-xs font-bold text-slate-700">{card.title}</span>
                </div>
                {card.helper && (
                  <div className={`mt-1 font-mono truncate text-[11px] font-bold ${theme.text}`}>
                    {card.helper}
                  </div>
                )}
              </div>
              <ChevronRight size={14} className="flex-shrink-0 text-slate-400" />
            </div>
          </Card>
        );
      })}
    </section>
  );
}

function AvailabilityByCity({ cities, onAllCities }) {
  const visibleCities = cities.slice(0, 6);

  return (
    <Card className="p-3">
      <SectionTitle
        title="Availability by city"
        subtitle="Occupied, available, and maintenance inventory"
        action="All cities"
        onAction={onAllCities}
      />

      <div className="space-y-2.5">
        {visibleCities.map((city) => {
          const cityTotal = asNumber(city.total);
          const total = Math.max(1, cityTotal);
          const segments = [
            { key: "occupied", label: "Occupied", short: "Occ", value: asNumber(city.occupied), color: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 ring-emerald-100" },
            { key: "available", label: "Available", short: "Avl", value: asNumber(city.available), color: "bg-rose-500", chip: "bg-rose-50 text-rose-700 ring-rose-100" },
            { key: "maintenance", label: "Maintenance", short: "Maint", value: asNumber(city.maintenance), color: "bg-sky-500", chip: "bg-sky-50 text-sky-700 ring-sky-100" },
          ];
          const visibleCounts = segments.filter((segment) => segment.value > 0);

          return (
            <div key={city.city} className="min-w-0">
              <div className="mb-1 flex items-center justify-between gap-3 text-xs">
                <span className="truncate font-bold text-slate-700">{city.city}</span>
                <span className="flex-shrink-0 font-semibold text-slate-500">{cityTotal} total</span>
              </div>
              <div className="flex h-3 overflow-hidden rounded bg-slate-100">
                {segments.map((segment) => {
                  if (!segment.value) return null;
                  const pct = (segment.value / total) * 100;

                  return (
                    <div
                      key={segment.key}
                      className={`${segment.color} min-w-[8px]`}
                      style={{ width: `${pct}%` }}
                      title={`${segment.label}: ${segment.value}`}
                    />
                  );
                })}
              </div>
              {visibleCounts.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {visibleCounts.map((segment) => (
                    <span
                      key={segment.key}
                      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wide ring-1 ${segment.chip}`}
                    >
                      {segment.short}
                      <strong className="font-black">{segment.value.toLocaleString("en-IN")}</strong>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {visibleCities.length === 0 && (
          <p className="py-12 text-center text-sm font-medium text-slate-400">No city inventory data yet.</p>
        )}
      </div>
    </Card>
  );
}

function RevenueTrend({ monthly }) {
  const rows = monthly.slice(-6).map((row) => ({
    ...row,
    label: formatCompactMonth(row.month),
    lakhs: Math.round(asNumber(row.revenue) / 100000),
  }));

  return (
    <Card className="p-3">
      <SectionTitle title="Revenue trend" subtitle="Last 6 months, shown in lakhs" />

      <div className="h-[172px] min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 10, left: -18, bottom: 0 }}>
            <CartesianGrid stroke="#e2e8f0" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "#64748b" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#64748b" }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              formatter={tooltipFormatter}
              contentStyle={{
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                boxShadow: "0 12px 28px rgba(15,23,42,0.10)",
                fontSize: 12,
              }}
            />
            <Line
              type="monotone"
              dataKey="lakhs"
              stroke="#2563eb"
              strokeWidth={2.3}
              dot={{ r: 3.5, fill: "#2563eb", stroke: "#2563eb" }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function CitiesCard({ cities, onOpen }) {
  const visible = cities.slice(0, 7);

  return (
    <Card className="p-3">
      <SectionTitle
        title="Cities"
        subtitle="Lowest occupancy first"
        action="Analytics"
        onAction={onOpen}
      />

      <div className="space-y-1.5">
        {visible.map((city) => {
          const pct = asNumber(city.occupancyPct);
          const total = asNumber(city.count || city.total);
          return (
            <button
              key={city.name || city.city}
              type="button"
              onClick={onOpen}
              className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-left transition hover:border-slate-500 hover:bg-slate-50"
            >
              <div className="min-w-0">
                <div className="truncate text-xs font-bold text-slate-800">{city.name || city.city}</div>
                <div className="mt-1 h-3 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={pct < 60 ? "h-full rounded-full bg-rose-500" : "h-full rounded-full bg-emerald-500"}
                    style={{ width: `${Math.max(4, Math.min(100, pct))}%` }}
                  />
                </div>
              </div>
              <div className="text-right">
                <div className={`text-sm font-black ${pct < 60 ? "text-rose-700" : "text-emerald-700"}`}>{pct}%</div>
                <div className="text-[10px] font-semibold text-slate-500">{asNumber(city.occupied)} / {total}</div>
              </div>
            </button>
          );
        })}

        {visible.length === 0 && (
          <p className="py-12 text-center text-sm font-medium text-slate-400">No city sales push data yet.</p>
        )}
      </div>
    </Card>
  );
}

function getPhotoUrl(item) {
  const photo = item?.photos?.[0] || item?.photo_metadata?.[0];
  if (typeof photo === "string") return photo;
  return photo?.url || item?.photo_url || item?.image_url || item?.images?.[0] || "";
}

function HoardingImage({ item }) {
  const url = getPhotoUrl(item);

  return (
    <span className="relative inline-flex h-8 w-10 flex-shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-100">
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
      ) : (
        <span className="h-full w-full bg-slate-100" />
      )}
    </span>
  );
}

function DataCard({ title, action, onAction, children, headerExtra }) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-100 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <SectionTitle title={title} action={action} onAction={onAction} />
          {headerExtra}
        </div>
      </div>
      <div className="overflow-x-auto p-3">{children}</div>
    </Card>
  );
}

function ProfitList({ title, rows, positive, nav, viewAllPath, rawAmounts, onToggleAmounts }) {
  return (
    <DataCard
      title={title}
      action="View all"
      onAction={() => nav(viewAllPath)}
      headerExtra={<AmountSwitch raw={rawAmounts} onToggle={onToggleAmounts} />}
    >
      <table className="w-full min-w-[620px] text-xs">
        <thead>
          <tr className="border-b border-slate-100 text-left text-[11px] font-bold text-slate-500">
            <th className="py-2 pr-3">Hoarding</th>
            <th className="py-2 pr-3">ID</th>
            <th className="py-2 pr-3">City</th>
            <th className="py-2 pr-3 text-right">Revenue</th>
            <th className="py-2 pr-3 text-right">Expenses</th>
            <th className="py-2 pr-3 text-right">{positive ? "Profit" : "Loss"}</th>
            <th className="py-2 text-right">ROI</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 5).map((row) => (
            <tr
              key={row.id}
              onClick={() => nav(`/hoardings/${row.id}`)}
              className="h-10 cursor-pointer border-b border-slate-100 transition last:border-0 hover:bg-slate-50"
            >
              <td className="max-w-[210px] pr-3">
                <div className="flex min-w-0 items-center gap-2">
                  <HoardingImage item={row} />
                  <span className="truncate font-bold text-slate-700">{row.name}</span>
                </div>
              </td>
              <td className="max-w-[96px] truncate pr-3 font-mono text-[11px] font-bold text-slate-500">
                {row.hoarding_id || row.display_hoarding_id || row.hoarding_code || row.code || "-"}
              </td>
              <td className="max-w-[110px] truncate pr-3 font-medium text-slate-600">{row.city || "-"}</td>
              <td className="whitespace-nowrap pr-3 text-right font-mono text-slate-700">{moneyLabel(row.revenue, rawAmounts)}</td>
              <td className="whitespace-nowrap pr-3 text-right font-mono text-slate-700">{moneyLabel(row.total_expenses, rawAmounts)}</td>
              <td className={`whitespace-nowrap pr-3 text-right font-mono font-bold ${positive ? "text-emerald-700" : "text-rose-700"}`}>
                {positive ? moneyLabel(row.profit, rawAmounts) : moneyLabel(-Math.abs(asNumber(row.profit)), rawAmounts)}
              </td>
              <td className={`whitespace-nowrap text-right font-mono font-bold ${positive ? "text-emerald-700" : "text-rose-700"}`}>
                {positive ? "" : "-"}
                {Math.abs(asNumber(row.roi_pct)).toFixed(0)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {rows.length === 0 && (
        <p className="py-12 text-center text-sm font-medium text-slate-400">
          No financial rows returned yet.
        </p>
      )}
    </DataCard>
  );
}

export function AdvertiserDashboard() {
  const [data, setData] = useState(null);
  const [todayFinance, setTodayFinance] = useState({
    receivables: [],
    rents: [],
    receivablesTotal: 0,
    receivablesAmount: 0,
    rentsTotal: 0,
    rentsAmount: 0,
  });
  const [error, setError] = useState("");
  const [rawAmounts, setRawAmounts] = useState(false);
  const nav = useNavigate();
  const { user } = useAuth();

  const canViewRevenue = hasAnyPermission(user, [
    "view_basic_revenue",
    "view_campaign_amounts",
    "view_profit_and_loss",
    "view_financial_reports",
  ]);

  const canViewProfit = hasAnyPermission(user, [
    "view_profit_and_loss",
    "view_financial_reports",
  ]);
  const canViewReceivables = hasAnyPermission(user, [
    "view_basic_revenue",
    "view_campaign_amounts",
    "view_profit_and_loss",
    "view_financial_reports",
  ]);
  const canViewRents = hasAnyPermission(user, [
    "view_rent_details",
    "view_expenses",
    "view_profit_and_loss",
    "view_financial_reports",
  ]);

  useEffect(() => {
    api
      .get("/analytics/dashboard", { skipGlobalLoader: true })
      .then((r) => {
        setData(normalizeDashboardData(r.data));
        setError("");
      })
      .catch((e) => {
        if (e.response?.status === 423) {
          setError("Your data migration is in progress. Dashboard will be available after import completes.");
          return;
        }
        setError(formatApiError(e.response?.data?.detail) || "Dashboard could not be loaded.");
      });
  }, []);

  useEffect(() => {
    if (!canViewReceivables && !canViewRents) {
      setTodayFinance({ receivables: [], rents: [], receivablesTotal: 0, receivablesAmount: 0, rentsTotal: 0, rentsAmount: 0 });
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const now = new Date();
    const yearFrom = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10);
    const monthFrom = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const monthTo = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
    const previewLimit = 20;
    Promise.all([
      canViewReceivables
        ? api.get(`/finance-ops/receivables?from=${yearFrom}&to=${today}&status=outstanding&limit=${previewLimit}`, { skipGlobalLoader: true }).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] }),
      canViewRents
        ? api.get(`/finance-ops/rent-calendar?from=${monthFrom}&to=${monthTo}&status=outstanding&limit=${previewLimit}`, { skipGlobalLoader: true }).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] }),
    ]).then(([receivables, rents]) => {
      const receivableRows = Array.isArray(receivables.data) ? receivables.data : (receivables.data?.items || []);
      const rentRows = Array.isArray(rents.data) ? rents.data : (rents.data?.items || []);
      setTodayFinance({
        receivables: receivableRows,
        rents: rentRows,
        receivablesTotal: asNumber(receivables.data?.total ?? receivableRows.filter((item) => item.payment_status !== "paid").length),
        receivablesAmount: asNumber(receivables.data?.total_amount ?? receivableRows.reduce((sum, item) => sum + asNumber(item.pending_amount), 0)),
        rentsTotal: asNumber(rents.data?.total ?? rentRows.filter((item) => item.status !== "paid").length),
        rentsAmount: asNumber(rents.data?.total_amount ?? rentRows.reduce((sum, item) => sum + asNumber(item.amount), 0)),
      });
    });
  }, [canViewReceivables, canViewRents]);

  const k = data?.kpis || {};
  const occupied = asNumber(k.occupied);
  const available = asNumber(k.available);
  const maintenance = asNumber(k.maintenance);
  const total = asNumber(k.total_hoardings);
  const occupancy = Math.round(asNumber(k.occupancy_pct));
  const mostProfitableRows = data?.most_profitable || [];
  const lossMakingRows = data?.loss_making || [];
  const toggleAmounts = () => setRawAmounts((value) => !value);

  const cityRows = useMemo(() => {
    const cities = data?.cities || [];
    return [...cities]
      .filter((city) => city.city)
      .sort((a, b) => asNumber(b.total) - asNumber(a.total));
  }, [data]);

  const salesPushCities = useMemo(() => {
    const cities = data?.city_counts || [];
    return [...cities].sort((a, b) => asNumber(a.occupancyPct) - asNumber(b.occupancyPct));
  }, [data]);

  const smartCards = useMemo(() => {
    if (!data) return [];
    const todayExpiring = (data.expiring_soon || []).filter((item) => daysUntil(item.campaign_end) === 0);
    const weekExpiring = (data.expiring_soon || []).filter((item) => {
      const left = daysUntil(item.campaign_end);
      return left !== null && left > 0 && left <= 7;
    });
    const receivableDue = todayFinance.receivables.filter((item) => item.payment_status !== "paid");
    const rentDue = todayFinance.rents.filter((item) => item.status !== "paid");
    const cards = [];

    if (todayFinance.receivablesTotal > 0 || receivableDue.length > 0) {
      const count = todayFinance.receivablesTotal || receivableDue.length;
      const amount = todayFinance.receivablesAmount || receivableDue.reduce((sum, item) => sum + asNumber(item.pending_amount), 0);
      cards.push({
        key: "receivables-today",
        title: "Receivable",
        value: count.toLocaleString("en-IN"),
        helper: moneyLabel(amount, false),
        icon: ReceiptIndianRupee,
        tone: "amber",
        onClick: () => nav("/finance?queue=receivables&due=until_today&status=outstanding"),
      });
    }
    if (todayFinance.rentsTotal > 0 || rentDue.length > 0) {
      const count = todayFinance.rentsTotal || rentDue.length;
      const amount = todayFinance.rentsAmount || rentDue.reduce((sum, item) => sum + asNumber(item.amount), 0);
      cards.push({
        key: "rent-today",
        title: "Rent payable",
        value: count.toLocaleString("en-IN"),
        helper: moneyLabel(amount, false),
        icon: WalletCards,
        tone: "rose",
        onClick: () => nav("/finance?queue=rents"),
      });
    }
    if (todayExpiring.length > 0) {
      cards.push({
        key: "expiring-today",
        title: "Expiring today",
        value: todayExpiring.length.toLocaleString("en-IN"),
        helper: "Campaign endings",
        icon: CalendarClock,
        tone: "amber",
        onClick: () => nav("/campaigns?status=Active&ending=today"),
      });
    }
    if (weekExpiring.length > 0) {
      cards.push({
        key: "expiring-week",
        title: "Expiring 7 days",
        value: weekExpiring.length.toLocaleString("en-IN"),
        helper: "Plan follow-up",
        icon: AlertTriangle,
        tone: "blue",
        onClick: () => nav("/campaigns?status=Active&ending=7d"),
      });
    }
    return cards;
  }, [data, nav, todayFinance]);

  return (
    <Layout
      title="Home"
      subtitle="Inventory, finance, and sales focus"
      mainClassName="bg-slate-50 p-0"
    >
      {error ? (
        <div className="m-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900 md:m-6">
          {error}
        </div>
      ) : !data ? (
        <div className="flex min-h-full items-center justify-center">
          <HoardexaLoader label="Loading dashboard" />
        </div>
      ) : (
        <div className="min-h-full w-full bg-slate-50 px-3 py-4 text-slate-950 sm:px-5 lg:px-6">
          <div className="hx-hoardings-page hx-home-dashboard">
            <MetricStrip
              metrics={[
                { label: "Total Inventory", value: total.toLocaleString("en-IN"), icon: LayoutDashboard, tone: "indigo" },
                { label: "Occupied", value: occupied.toLocaleString("en-IN"), icon: WalletCards, tone: "emerald", onClick: () => nav("/hoardings?status=Occupied") },
                { label: "Available", value: available.toLocaleString("en-IN"), icon: MonitorCheck, tone: "rose", onClick: () => nav("/hoardings?status=Available") },
                { label: "Maintenance", value: maintenance.toLocaleString("en-IN"), icon: Wrench, tone: "blue", onClick: () => nav("/hoardings?status=Maintenance") },
                { label: "Occupancy", value: `${occupancy}%`, icon: PieChart, tone: "slate" },
              ]}
            />

            {canViewRevenue && (
              <section className="grid grid-cols-1 gap-3">
                <FinanceTile
                  kpis={k}
                  rawAmounts={rawAmounts}
                  onToggleAmounts={toggleAmounts}
                />
              </section>
            )}

            <SmartActionGrid cards={smartCards} />

            <section className={`grid grid-cols-1 items-start gap-3.5 ${canViewRevenue ? "xl:grid-cols-[minmax(0,0.96fr)_minmax(0,1.04fr)]" : "xl:grid-cols-2"}`}>
              <div className="grid min-w-0 gap-3.5">
                <AvailabilityByCity cities={cityRows} onAllCities={() => nav("/city-analytics")} />
                {canViewRevenue && (
                  <RevenueTrend monthly={data.monthly || []} />
                )}
              </div>

              <div className="grid min-w-0 gap-3.5">
                <CitiesCard cities={salesPushCities} onOpen={() => nav("/city-analytics")} />
              </div>
            </section>

            {canViewProfit && (
              <section className="grid grid-cols-1 gap-3.5 2xl:grid-cols-2">
                <ProfitList
                  title="Most profitable hoardings"
                  rows={mostProfitableRows}
                  positive
                  nav={nav}
                  viewAllPath="/hoardings?insight=most_profitable&rows=25"
                  rawAmounts={rawAmounts}
                  onToggleAmounts={toggleAmounts}
                />
                <ProfitList
                  title="Loss-making hoardings"
                  rows={lossMakingRows}
                  positive={false}
                  nav={nav}
                  viewAllPath="/hoardings?insight=loss_making&rows=25"
                  rawAmounts={rawAmounts}
                  onToggleAmounts={toggleAmounts}
                />
              </section>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
